import { v7 as uuidv7 } from 'uuid'
import hash from 'object-hash'
import { match, P } from 'ts-pattern'

import { InstalledSoftwareApi } from 'openapi'
import { appAPI, isApp } from 'utils/app-api'

export type InstalledSoftwareTrackingType = 'deny' | 'anonymous'

export class InstalledSoftwareService {
  static readonly ALLOW_INSTALLED_TRACKING_KEY = 'PRESYNC_ALLOW_INSTALLED_TRACKING'
  static readonly USER_UUID_TRACKING_KEY = 'USER_UUID_TRACKING_KEY'
  static readonly HASH_KEY = 'INSTALLED_SOFTWARE_HASH'

  static generateUserUuid(): string {
    return uuidv7()
  }

  static async saveInstalledSoftware(userUuid: string): Promise<void> {
    if (!isApp) return

    const installedComponents = await appAPI.getInstalledComponents()
    if (!installedComponents?.length) return

    const installedComponentsDTO = installedComponents.map((component) => ({
      filename: component.componentPath.split('/').pop() ?? '',
      version: component.version,
    }))

    const currentHash = localStorage.getItem(this.HASH_KEY)
    const newHash = hash(installedComponentsDTO)
    if (newHash === currentHash) return

    await InstalledSoftwareApi.saveInstalledSoftware({
      requestBody: { userUuid, installedComponents: installedComponentsDTO },
    })

    localStorage.setItem(this.HASH_KEY, newHash)
  }

  static checkInstalledSoftware(authToken: string): void {
    if (!isApp) return
    // TODO: This IPC call is not in the appAPI interface - needs investigation
    window.electron.ipcRenderer.send('installed-software-check', { authToken })
  }

  static getAllowInstalledTracking(): InstalledSoftwareTrackingType | undefined {
    const value = localStorage.getItem(this.ALLOW_INSTALLED_TRACKING_KEY)

    const result = match(value)
      .with(P.nullish, () => 'anonymous' as const)
      .with('anonymous', 'deny', (val: string) => val as InstalledSoftwareTrackingType)
      // old values
      .with('true', () => 'anonymous' as const)
      .with('false', () => 'deny' as const)
      .otherwise(() => 'deny' as const)

    if (result !== value) {
      this.setAllowInstalledTracking(result)
    }

    return result
  }

  static setAllowInstalledTracking(value: InstalledSoftwareTrackingType): void {
    localStorage.setItem(this.ALLOW_INSTALLED_TRACKING_KEY, value)
  }

  static getUserUuid(): string {
    let uuid = localStorage.getItem(this.USER_UUID_TRACKING_KEY)
    if (!uuid) {
      uuid = this.generateUserUuid()
      localStorage.setItem(this.USER_UUID_TRACKING_KEY, uuid)
    }
    // somehow we have "" stored sometimes :/
    return uuid.replaceAll('"', '')
  }
}
